/**
 * @file Pure 2D polygon helpers on the XZ ground plane. ZERO three.js imports —
 * this module is plain math so generators, the bake and the tests can use it
 * without touching the renderer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONVENTIONS (read once, then never think about it again)
 * ─────────────────────────────────────────────────────────────────────────────
 * • A polygon is `[[x, z], [x, z], ...]` — world XZ, no y, not closed (do NOT
 *   repeat the first point at the end).
 * • "CCW" here means **positive shoelace area in (x, z)**, i.e.
 *   `polygonArea(poly) > 0`. That is the orientation {@link rectToPoly} and
 *   {@link convexHull2D} produce, and the one {@link offsetPolygon} and
 *   `prims.prismFromPolygon` expect. Everything normalises internally, so a
 *   clockwise polygon still works — it just costs a reversal.
 * • Outward edge normal for a positively-wound polygon, for edge `p_i -> p_i+1`
 *   with direction `(ex, ez)`, is `(ez, -ex)`. (Used by {@link offsetPolygon}
 *   and by the prism wall winding — keep them in sync.)
 * • A Rect is `{x, z, w, d}` where **`x,z` is the MINIMUM corner**, not the
 *   centre, and `w,d` are extents along +X and +Z.
 *
 * @module engine/build/polygon
 */

/** @typedef {[number, number]} P2 A point as `[x, z]`. */
/** @typedef {P2[]} Poly */
/** @typedef {{ x: number, z: number, w: number, d: number }} Rect */

const EPS = 1e-9;

// ─────────────────────────────────────────────────────────────────────────────
// Measurement
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Signed shoelace area. Positive => the polygon is wound the way the rest of
 * this module expects (see the module header).
 * @param {Poly} poly
 * @returns {number} Signed area in square metres.
 */
export function polygonArea(poly) {
  const n = poly.length;
  if (n < 3) return 0;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s * 0.5;
}

/**
 * Area-weighted centroid. Falls back to the vertex average for degenerate
 * (zero-area) input so callers never get NaN.
 * @param {Poly} poly
 * @returns {P2}
 */
export function polygonCentroid(poly) {
  const n = poly.length;
  if (n === 0) return [0, 0];
  if (n < 3) return vertexAverage(poly);

  let cx = 0;
  let cz = 0;
  let a2 = 0;
  for (let i = 0; i < n; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % n];
    const cr = p[0] * q[1] - q[0] * p[1];
    a2 += cr;
    cx += (p[0] + q[0]) * cr;
    cz += (p[1] + q[1]) * cr;
  }
  if (Math.abs(a2) < EPS) return vertexAverage(poly);
  const inv = 1 / (3 * a2);
  return [cx * inv, cz * inv];
}

/**
 * @param {Poly} poly
 * @returns {P2}
 */
function vertexAverage(poly) {
  let x = 0;
  let z = 0;
  for (const p of poly) {
    x += p[0];
    z += p[1];
  }
  const inv = poly.length ? 1 / poly.length : 0;
  return [x * inv, z * inv];
}

/**
 * Axis-aligned bounds of a polygon. (Extension beyond the T3 spec — generators
 * want it constantly for chunk/collider derivation.)
 * @param {Poly} poly
 * @returns {{ minX: number, minZ: number, maxX: number, maxZ: number }}
 */
export function polygonBounds(poly) {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const p of poly) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minZ) minZ = p[1];
    if (p[1] > maxZ) maxZ = p[1];
  }
  if (!poly.length) return { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
  return { minX, minZ, maxX, maxZ };
}

/**
 * Crossing-number point-in-polygon. Points exactly on an edge are undefined
 * (may go either way) — that is fine for placement masks.
 * @param {Poly} poly
 * @param {number} x
 * @param {number} z
 * @returns {boolean}
 */
export function pointInPoly(poly, x, z) {
  const n = poly.length;
  if (n < 3) return false;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i][0];
    const zi = poly[i][1];
    const xj = poly[j][0];
    const zj = poly[j][1];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

// ─────────────────────────────────────────────────────────────────────────────
// Triangulation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {number} ax @param {number} az
 * @param {number} bx @param {number} bz
 * @param {number} cx @param {number} cz
 * @returns {number} 2x the signed area of triangle abc.
 */
const cross2 = (ax, az, bx, bz, cx, cz) => (bx - ax) * (cz - az) - (bz - az) * (cx - ax);

/**
 * Ear clipping. Returns triangles as INDICES INTO THE INPUT ARRAY, always wound
 * positively (same convention as {@link polygonArea} > 0), regardless of the
 * input winding.
 *
 * Simple polygons only — no holes, no self-intersections. Self-intersecting
 * input degrades to a fan of the leftovers rather than hanging.
 *
 * @param {Poly} poly
 * @returns {Array<[number, number, number]>}
 */
export function triangulatePolygon(poly) {
  const n = poly.length;
  if (n < 3) return [];
  if (n === 3) return polygonArea(poly) >= 0 ? [[0, 1, 2]] : [[0, 2, 1]];

  // Work on an index ring in positive orientation; indices still point at the
  // caller's array, so the winding fix is free.
  /** @type {number[]} */
  const ring = [];
  if (polygonArea(poly) >= 0) for (let i = 0; i < n; i++) ring.push(i);
  else for (let i = n - 1; i >= 0; i--) ring.push(i);

  /** @type {Array<[number, number, number]>} */
  const tris = [];
  let guard = n * n * 2 + 32;

  while (ring.length > 3 && guard-- > 0) {
    let clipped = false;
    for (let i = 0; i < ring.length; i++) {
      const m = ring.length;
      const i0 = ring[(i + m - 1) % m];
      const i1 = ring[i];
      const i2 = ring[(i + 1) % m];
      if (isEar(poly, ring, i0, i1, i2)) {
        tris.push([i0, i1, i2]);
        ring.splice(i, 1);
        clipped = true;
        break;
      }
    }
    if (!clipped) break; // degenerate — fan whatever is left below
  }

  if (ring.length === 3) tris.push([ring[0], ring[1], ring[2]]);
  else if (ring.length > 3) {
    for (let i = 1; i < ring.length - 1; i++) tris.push([ring[0], ring[i], ring[i + 1]]);
  }
  return tris;
}

/**
 * @param {Poly} poly
 * @param {number[]} ring
 * @param {number} i0
 * @param {number} i1
 * @param {number} i2
 * @returns {boolean}
 */
function isEar(poly, ring, i0, i1, i2) {
  const a = poly[i0];
  const b = poly[i1];
  const c = poly[i2];
  const area = cross2(a[0], a[1], b[0], b[1], c[0], c[1]);
  if (area <= EPS) return false; // reflex or collinear

  for (let k = 0; k < ring.length; k++) {
    const idx = ring[k];
    if (idx === i0 || idx === i1 || idx === i2) continue;
    const p = poly[idx];
    if (pointInTri(p[0], p[1], a, b, c)) return false;
  }
  return true;
}

/**
 * @param {number} px @param {number} pz
 * @param {P2} a @param {P2} b @param {P2} c
 * @returns {boolean}
 */
function pointInTri(px, pz, a, b, c) {
  const d0 = cross2(a[0], a[1], b[0], b[1], px, pz);
  const d1 = cross2(b[0], b[1], c[0], c[1], px, pz);
  const d2 = cross2(c[0], c[1], a[0], a[1], px, pz);
  return d0 >= 0 && d1 >= 0 && d2 >= 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Offsetting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Miter offset. **`d > 0` grows the polygon, `d < 0` insets it** (setbacks,
 * sidewalk curbs, wall thickness). Convex-biased: concave corners on strongly
 * non-convex polygons can self-intersect, which is acceptable for the shapes
 * this framework generates (rects, L-shapes, notched footprints).
 *
 * Returns `[]` when the offset collapses the polygon (area hits zero or flips
 * sign) — callers should treat that as "the lot is too small, skip it".
 *
 * NOTE the sign convention differs from {@link insetRect}, where a POSITIVE
 * amount shrinks. Offsetting is a signed displacement; insetting is a distance.
 *
 * @param {Poly} poly
 * @param {number} d Metres. Negative = inset.
 * @param {{ miterLimit?: number }} [opts] `miterLimit` caps the corner spike at
 *   `|d| * miterLimit` (default 6).
 * @returns {Poly}
 */
export function offsetPolygon(poly, d, { miterLimit = 6 } = {}) {
  const n = poly.length;
  if (n < 3) return [];
  if (d === 0) return poly.map((p) => /** @type {P2} */ ([p[0], p[1]]));

  const area0 = polygonArea(poly);
  const flipped = area0 < 0;
  const src = flipped ? poly.slice().reverse() : poly;

  /** @type {Poly} */
  const out = [];
  for (let i = 0; i < n; i++) {
    const p0 = src[(i + n - 1) % n];
    const p1 = src[i];
    const p2 = src[(i + 1) % n];

    let e0x = p1[0] - p0[0];
    let e0z = p1[1] - p0[1];
    let e1x = p2[0] - p1[0];
    let e1z = p2[1] - p1[1];
    const l0 = Math.hypot(e0x, e0z) || 1;
    const l1 = Math.hypot(e1x, e1z) || 1;
    e0x /= l0;
    e0z /= l0;
    e1x /= l1;
    e1z /= l1;

    // Outward normals for a positively-wound polygon.
    const n0x = e0z;
    const n0z = -e0x;
    const n1x = e1z;
    const n1z = -e1x;

    let mx = n0x + n1x;
    let mz = n0z + n1z;
    const ml = Math.hypot(mx, mz);
    if (ml < 1e-7) {
      // 180-degree spike: no usable miter, ride the outgoing edge normal.
      out.push([p1[0] + n1x * d, p1[1] + n1z * d]);
      continue;
    }
    mx /= ml;
    mz /= ml;

    // |miter| = d / cos(theta/2), and cos(theta/2) = dot(miterDir, edgeNormal).
    let cosHalf = mx * n1x + mz * n1z;
    if (Math.abs(cosHalf) < 1e-6) cosHalf = cosHalf < 0 ? -1e-6 : 1e-6;
    let len = d / cosHalf;
    const lim = Math.abs(d) * miterLimit;
    if (len > lim) len = lim;
    else if (len < -lim) len = -lim;

    out.push([p1[0] + mx * len, p1[1] + mz * len]);
  }

  const res = flipped ? out.reverse() : out;
  const area1 = polygonArea(res);
  if (Math.abs(area1) < EPS) return [];
  if (area1 > 0 !== area0 > 0) return []; // inverted through itself

  // The sign test alone is not enough: inset a square past its own half-width
  // and the four corners swap places, producing a SMALLER square that is still
  // wound the same way. The give-away is that an edge has reversed direction —
  // check every edge against its original.
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = poly[j][0] - poly[i][0];
    const az = poly[j][1] - poly[i][1];
    const bx = res[j][0] - res[i][0];
    const bz = res[j][1] - res[i][1];
    if (ax * ax + az * az < EPS) continue; // degenerate source edge
    if (ax * bx + az * bz < 0) return []; // this edge folded through itself
  }
  return res;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rects
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {Rect} rect
 * @returns {Poly} Positively wound (min corner first, then +X, then +Z).
 */
export function rectToPoly(rect) {
  const { x, z, w, d } = rect;
  return [
    [x, z],
    [x + w, z],
    [x + w, z + d],
    [x, z + d],
  ];
}

/**
 * Shrinks a rect by `amount` on ALL FOUR sides, keeping its centre.
 * **A positive `amount` shrinks** (this is a distance, not a signed offset —
 * contrast {@link offsetPolygon}). Never returns negative extents; a fully
 * collapsed rect comes back with `w === 0` / `d === 0`, which callers should
 * test for before generating anything.
 *
 * @param {Rect} rect
 * @param {number} amount Metres of setback per side.
 * @returns {Rect}
 */
export function insetRect(rect, amount) {
  const w = Math.max(0, rect.w - amount * 2);
  const d = Math.max(0, rect.d - amount * 2);
  return {
    x: rect.x + (rect.w - w) * 0.5,
    z: rect.z + (rect.d - d) * 0.5,
    w,
    d,
  };
}

/**
 * Splits a rect in two along an axis. The recursive-lot-subdivision primitive.
 * @param {Rect} rect
 * @param {'x'|'z'} axis Axis to cut ALONG (i.e. `'x'` produces a left/right pair).
 * @param {number} t Split fraction 0..1 of the extent on that axis.
 * @returns {[Rect, Rect]}
 */
export function splitRect(rect, axis, t) {
  const f = t < 0 ? 0 : t > 1 ? 1 : t;
  if (axis === 'x') {
    const w0 = rect.w * f;
    return [
      { x: rect.x, z: rect.z, w: w0, d: rect.d },
      { x: rect.x + w0, z: rect.z, w: rect.w - w0, d: rect.d },
    ];
  }
  const d0 = rect.d * f;
  return [
    { x: rect.x, z: rect.z, w: rect.w, d: d0 },
    { x: rect.x, z: rect.z + d0, w: rect.w, d: rect.d - d0 },
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Hull
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Andrew's monotone chain. This is what the projected hard sun-shadow polygons
 * are built from (`hull(footprint ∪ footprint + h·(−L.xz/L.y))`).
 * @param {Array<P2|{x: number, z: number}>} points
 * @returns {Poly} Positively wound hull. Fewer than 3 unique inputs come back
 *   as-is (deduped), so callers must tolerate a 0/1/2-point result.
 */
export function convexHull2D(points) {
  /** @type {Poly} */
  const pts = [];
  for (const p of points) {
    if (Array.isArray(p)) pts.push([p[0], p[1]]);
    else pts.push([p.x, p.z]);
  }
  pts.sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  /** @type {Poly} */
  const uniq = [];
  for (const p of pts) {
    const q = uniq[uniq.length - 1];
    if (!q || q[0] !== p[0] || q[1] !== p[1]) uniq.push(p);
  }
  const n = uniq.length;
  if (n < 3) return uniq;

  const turn = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  /** @type {Poly} */
  const lower = [];
  for (const p of uniq) {
    while (lower.length >= 2 && turn(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  /** @type {Poly} */
  const upper = [];
  for (let i = n - 1; i >= 0; i--) {
    const p = uniq[i];
    while (upper.length >= 2 && turn(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}
